import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'

import { keys } from '@/api/keys'
import { agyModelsQuery, patchSettings, settingsQuery, testAntigravity } from '@/api/settings'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatMs } from '@/lib/format'

const message = (error: unknown) => error instanceof Error ? error.message : String(error)

export function AntigravitySettings() {
  const qc = useQueryClient()
  const settings = useQuery(settingsQuery)
  const models = useQuery(agyModelsQuery)
  const [draft, setDraft] = useState<string | null>(null)
  const id = useId()
  const saved = settings.data?.ai.agy?.model ?? models.data?.selected_model ?? ''
  const selected = draft ?? saved
  const items = models.data?.items ?? []
  const dirty = selected !== saved
  const available = items.some(item => item.id === selected)

  const ping = useMutation({
    mutationFn: testAntigravity,
    onSettled: () => { void qc.invalidateQueries({ queryKey: keys.health() }) },
  })
  const save = useMutation({
    mutationFn: (model: string) => patchSettings({ ai: { agy: { model } } }),
    onSuccess: next => {
      qc.setQueryData(keys.settings(), next)
      setDraft(null)
      ping.reset()
      void qc.invalidateQueries({ queryKey: keys.agyModels() })
      void qc.invalidateQueries({ queryKey: keys.health() })
    },
  })
  const busy = save.isPending || ping.isPending

  return (
    <section className="card p-5" aria-labelledby={`${id}-heading`}>
      <h2 id={`${id}-heading`} className="text-lg font-semibold text-ink">Antigravity CLI</h2>
      <p className="mt-1 text-sm text-ink-2">
        {settings.isPending ? 'Loading connection settings…' : settings.data?.ai.agy?.enabled
          ? 'Antigravity is the selected AI provider. Every call goes directly to the saved model.'
          : 'Choose a model, then select Antigravity as your AI provider above to use it. There is no automatic fallback.'}
      </p>
      <div className="mt-4">
        <Field label="Antigravity model" htmlFor={`${id}-model`}>
          <Select value={selected} onValueChange={value => { setDraft(value); save.reset(); ping.reset() }} disabled={busy || models.isPending || models.isError || !items.length || settings.isError}>
            <SelectTrigger id={`${id}-model`} aria-label="Antigravity model" aria-describedby={`${id}-hint`}>
              <SelectValue placeholder={models.isPending ? 'Loading models…' : 'Choose a model'} />
            </SelectTrigger>
            <SelectContent>
              {selected && !available ? <SelectItem value={selected} disabled>{selected} (not in current catalog)</SelectItem> : null}
              {items.map(item => <SelectItem key={item.id} value={item.id}>{item.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <p id={`${id}-hint`} className="mt-1.5 text-xs text-ink-3">Models come from your installed AGY CLI. Save before testing; existing videos keep their recorded model.</p>
        <p className="mt-2 break-all text-sm text-ink-2" role="status">{saved ? `Saved model: ${saved}` : 'No saved model is available yet.'}</p>
      </div>
      {settings.isError ? <p className="mt-3 text-sm text-danger" role="alert">Could not load settings. {message(settings.error)} <button className="underline" onClick={() => void settings.refetch()}>Reload settings</button></p> : null}
      {models.isError ? <p className="mt-3 text-sm text-danger" role="alert">Could not load Antigravity models. {message(models.error)} Reload the models after the bridge is available.</p> : null}
      {models.isSuccess && !items.length ? <p className="mt-3 text-sm text-ink-2">The CLI returned no models. Check its login, then reload models.</p> : null}
      {save.isError ? <p className="mt-3 text-sm text-danger" role="alert">Could not save the model. {message(save.error)} Your saved choice is unchanged.</p> : null}
      {ping.isError ? <p className="mt-3 text-sm text-danger" role="alert">The test did not complete. {message(ping.error)} Check Antigravity and try again.</p> : null}
      {ping.data ? <p className="mt-3 text-sm text-ink-2" role="status">{ping.data.ok ? `Answered with ${ping.data.model} in ${formatMs(ping.data.duration_ms)}.` : 'Antigravity did not confirm a successful test.'}</p> : null}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={() => save.mutate(selected)} disabled={!dirty || !available || models.isFetching || models.isError || !settings.data || settings.isError || busy} loading={save.isPending}>Save Antigravity model</Button>
        <Button variant="secondary" onClick={() => ping.mutate()} disabled={!saved || dirty || busy || settings.isError} loading={ping.isPending}>Test Antigravity</Button>
        <Button variant="ghost" onClick={() => void models.refetch()} loading={models.isFetching} disabled={busy}>Reload Antigravity models</Button>
      </div>
      <p className="mt-2 text-xs text-ink-3">Testing sends one real request through AGY and may consume your account quota.</p>
    </section>
  )
}
